import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Truck, ShieldCheck, Sparkles, Dices, Mail } from "lucide-react";
import { useLang } from "../i18n";
import { getProducts, getCategories } from "../lib/api";
import { OBI, PLACEHOLDERS } from "../assets/obi";
import ProductCard from "../components/ProductCard";
import ObiPicksSection from "../components/ObiPicksSection";
import { ProductSkeleton } from "../components/Loaders";

const HomePage = () => {
  const { t } = useLang();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [prods, cats] = await Promise.all([
          getProducts({ per_page: 8, orderby: "popularity" }),
          getCategories(),
        ]);
        setProducts(prods);
        setCategories(cats.filter((c) => c.slug !== "uncategorized").slice(0, 6));
      } catch (e) {
        console.error("Home load error", e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const catColors = [
    "bg-sky-300",
    "bg-pink-300",
    "bg-yellow-300",
    "bg-emerald-300",
    "bg-violet-300",
    "bg-orange-300",
  ];

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden border-b-4 border-slate-900 bg-amber-50 bg-dots">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-24 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 space-y-6">
            <span
              data-testid="hero-eyebrow"
              className="inline-flex items-center gap-2 bg-pink-300 border-4 border-slate-900 rounded-full px-4 py-1.5 font-bold text-sm cartoon-shadow-sm marquee-wiggle"
            >
              <Sparkles className="w-4 h-4" /> {t("home.hero_eyebrow")}
            </span>
            <h1 className="font-display font-bold text-slate-900 leading-[0.95]">
              <span className="block text-5xl sm:text-6xl lg:text-7xl">
                {t("home.hero_pretitle")}
              </span>
              <span className="block text-3xl sm:text-4xl lg:text-5xl mt-3">
                {t("home.hero_title_1")}{" "}
                <span className="inline-block bg-yellow-300 border-4 border-slate-900 rounded-2xl px-3 -rotate-1 cartoon-shadow">
                  {t("home.hero_title_2")}
                </span>
              </span>
              <span className="block text-2xl sm:text-3xl lg:text-4xl mt-3 text-pink-500">
                {t("home.hero_title_3")} 🐶
              </span>
            </h1>
            <p className="text-lg lg:text-xl text-slate-700 max-w-xl leading-relaxed">
              {t("home.hero_subtitle")}
            </p>
            <div className="flex flex-wrap gap-4 pt-2">
              <Link
                to="/catalog"
                data-testid="hero-cta-shop"
                className="cartoon-btn bg-sky-400 hover:bg-sky-300 text-lg py-4 px-7"
              >
                {t("home.cta_shop")}
                <ArrowRight className="w-5 h-5" strokeWidth={3} />
              </Link>
              <a
                href="#categories"
                className="cartoon-btn bg-white hover:bg-pink-100 text-lg py-4 px-7"
              >
                {t("home.cta_secondary")}
              </a>
            </div>
          </div>
          <div className="lg:col-span-5 relative">
            <div className="absolute -top-6 -left-6 w-24 h-24 bg-yellow-300 rounded-full border-4 border-slate-900 cartoon-shadow hidden sm:block" />
            <div className="absolute -bottom-4 -right-4 w-16 h-16 bg-pink-400 rounded-full border-4 border-slate-900 cartoon-shadow hidden sm:block" />
            <div className="relative bg-gradient-to-br from-sky-200 to-pink-200 border-4 border-slate-900 rounded-[2.5rem] cartoon-shadow-lg overflow-hidden">
              <img
                src={OBI.hero}
                alt="OBI il golden retriever che gioca a un gioco da tavolo"
                className="w-full h-full object-cover animate-float"
              />
            </div>
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="bg-yellow-300 border-b-4 border-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { Icon: Truck, title: t("home.benefit_1_title"), text: t("home.benefit_1_text"), bg: "bg-pink-300" },
            { Icon: ShieldCheck, title: t("home.benefit_2_title"), text: t("home.benefit_2_text"), bg: "bg-sky-300" },
            { Icon: Sparkles, title: t("home.benefit_3_title"), text: t("home.benefit_3_text"), bg: "bg-emerald-300" },
          ].map(({ Icon, title, text, bg }) => (
            <div key={title} className="flex items-center gap-4 bg-white border-4 border-slate-900 rounded-2xl p-4 cartoon-shadow">
              <div className={`w-14 h-14 ${bg} border-4 border-slate-900 rounded-full flex items-center justify-center shrink-0`}>
                <Icon className="w-6 h-6 text-slate-900" strokeWidth={2.5} />
              </div>
              <div>
                <div className="font-display font-bold text-lg leading-tight">{title}</div>
                <div className="text-sm text-slate-600">{text}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CATEGORIES */}
      <section id="categories" className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
            <div>
              <h2 className="font-display font-bold text-4xl lg:text-5xl text-slate-900">
                {t("home.categories_title")}
              </h2>
              <p className="text-slate-600 mt-2 text-lg">{t("home.categories_subtitle")}</p>
            </div>
            <Link to="/catalog" className="cartoon-btn bg-white hover:bg-yellow-200 text-sm">
              {t("home.cta_shop")} <ArrowRight className="w-4 h-4" strokeWidth={3} />
            </Link>
          </div>
          {categories.length === 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {["Strategici","Famiglia","Bambini","Party","Cooperativi","Carte"].map((label, i) => (
                <Link
                  key={label}
                  to="/catalog"
                  className={`${catColors[i]} border-4 border-slate-900 rounded-3xl p-5 cartoon-shadow hover:cartoon-shadow-lg hover:-translate-y-1 transition-all flex flex-col items-center text-center gap-3`}
                >
                  <Dices className="w-10 h-10" strokeWidth={2.2} />
                  <span className="font-display font-bold text-lg">{label}</span>
                </Link>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {categories.map((c, i) => (
                <Link
                  key={c.id}
                  data-testid={`category-card-${c.id}`}
                  to={`/catalog?category=${c.id}`}
                  className={`${catColors[i % catColors.length]} border-4 border-slate-900 rounded-3xl p-5 cartoon-shadow hover:cartoon-shadow-lg hover:-translate-y-1 transition-all flex flex-col items-center text-center gap-3`}
                >
                  <Dices className="w-10 h-10" strokeWidth={2.2} />
                  <span className="font-display font-bold text-lg leading-tight">{c.name}</span>
                  <span className="text-xs font-bold opacity-70">{c.count} {t("catalog.results_count")}</span>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      <section className="bg-sky-100 border-y-4 border-slate-900 py-16 lg:py-24 bg-stripes">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
            <div>
              <h2 className="font-display font-bold text-4xl lg:text-5xl text-slate-900">
                {t("home.featured_title")}
              </h2>
              <p className="text-slate-600 mt-2 text-lg">{t("home.featured_subtitle")}</p>
            </div>
          </div>
          {loading ? (
            <ProductSkeleton count={8} />
          ) : products.length === 0 ? (
            <div className="cartoon-card p-12 text-center max-w-xl mx-auto">
              <img src={OBI.empty} alt="OBI" className="w-40 h-40 mx-auto" />
              <h3 className="font-display font-bold text-2xl mt-4">Coming soon!</h3>
              <p className="text-slate-600 mt-2">
                I prodotti compariranno qui non appena saranno disponibili nel tuo WooCommerce.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 sm:gap-8">
              {products.map((p, i) => (
                <ProductCard key={p.id} product={p} index={i} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* OBI'S PICKS */}
      <ObiPicksSection />

      {/* GIFT FINDER BANNER */}
      <section className="py-12 lg:py-16 bg-pink-100 border-y-4 border-slate-900 bg-stripes">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="cartoon-card bg-white p-6 lg:p-10 flex flex-col sm:flex-row items-center gap-6">
            <img src={OBI.shopping} alt="OBI" className="w-32 h-32 sm:w-40 sm:h-40 object-contain shrink-0 animate-float" />
            <div className="flex-1 text-center sm:text-left">
              <span className="inline-block bg-yellow-300 border-4 border-slate-900 rounded-full px-3 py-1 font-bold text-sm cartoon-shadow-sm mb-3">
                🎁 {t("nav.gift")}
              </span>
              <h3 className="font-display font-bold text-2xl lg:text-3xl text-slate-900 leading-tight">
                {t("gift.hero_title")}
              </h3>
              <p className="text-slate-700 mt-2">{t("gift.hero_subtitle")}</p>
            </div>
            <Link to="/regalo-perfetto" data-testid="home-gift-cta" className="cartoon-btn bg-pink-400 hover:bg-pink-300 text-lg py-3 px-6 shrink-0">
              {t("gift.cta_start")} <ArrowRight className="w-5 h-5" strokeWidth={3} />
            </Link>
          </div>
        </div>
      </section>

      {/* NEWSLETTER */}
      <section className="py-16 lg:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="cartoon-card bg-pink-200 p-8 lg:p-12 text-center">
            <Mail className="w-10 h-10 mx-auto" strokeWidth={2.5} />
            <h2 className="font-display font-bold text-3xl lg:text-4xl mt-4 text-slate-900">
              {t("home.newsletter_title")}
            </h2>
            <p className="text-slate-700 mt-2">{t("home.newsletter_subtitle")}</p>
            <form
              className="mt-6 flex flex-col sm:flex-row gap-3 max-w-lg mx-auto"
              onSubmit={(e) => e.preventDefault()}
              data-testid="newsletter-form"
            >
              <input
                type="email"
                placeholder="email@example.com"
                className="cartoon-input flex-1"
                data-testid="newsletter-input"
              />
              <button type="submit" className="cartoon-btn bg-yellow-300 hover:bg-yellow-200">
                {t("home.newsletter_btn")}
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
